import { useEffect, useMemo, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { BsStarFill, BsStar } from "react-icons/bs";
import { getAuth } from "firebase/auth";
import { useTranslation } from "react-i18next";
import "../../styles/LeaveCommentSheet.css";
import { loginWithGoogle } from "../../services/auth.service";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.VITE_API_BASE ||
  "http://localhost:8080";

const ANIM_MS = 450;
const CLOSE_DRAG_THRESHOLD = 120;
const MAX_DRAG_Y = 420;

function normalizeLang(lang) {
  return String(lang || "").toLowerCase().startsWith("en") ? "en" : "uk";
}

export default function LeaveCommentSheet({
  open,
  onClose,
  product,
  onCreated,
  topOffset = 84,
}) {
  const { t, i18n } = useTranslation("product");

  const sheetRef = useRef(null);
  const dragStartYRef = useRef(0);
  const dragDeltaYRef = useRef(0);
  const draggingRef = useRef(false);

  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [dragY, setDragY] = useState(0);

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const lang = normalizeLang(i18n.resolvedLanguage || i18n.language || "uk");

  const productTitle = useMemo(() => {
    return product?.name || t("fallback_product");
  }, [product, t]);

  const productId = useMemo(() => {
    return (
      product?.template_id ||
      product?.variant_id ||
      product?.product_id ||
      product?.id ||
      null
    );
  }, [product]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      setDragY(0);
      draggingRef.current = false;
      dragDeltaYRef.current = 0;
      return;
    }

    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
        setDragY(0);
      }, ANIM_MS);

      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;

    const auth = getAuth();
    const user = auth.currentUser;

    setRating(0);
    setHoveredRating(0);
    setComment("");
    setError("");

    setName(user?.displayName || "");
    setEmail(user?.email || "");
  }, [open]);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }

    if (mounted) {
      document.addEventListener("keydown", onKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  function clamp(n, min, max) {
    return Math.max(min, Math.min(n, max));
  }

  function handleDragStart(clientY) {
    if (closing) return;
    draggingRef.current = true;
    dragStartYRef.current = clientY;
    dragDeltaYRef.current = 0;
  }

  function handleDragMove(clientY) {
    if (!draggingRef.current) return;

    const delta = clientY - dragStartYRef.current;
    const next = clamp(delta, 0, MAX_DRAG_Y);

    dragDeltaYRef.current = next;
    setDragY(next);
  }

  function handleDragEnd() {
    if (!draggingRef.current) return;

    draggingRef.current = false;
    const shouldClose = dragDeltaYRef.current > CLOSE_DRAG_THRESHOLD;

    if (shouldClose) {
      setDragY(0);
      onClose?.();
      return;
    }

    dragDeltaYRef.current = 0;
    setDragY(0);
  }

  function onPointerDown(e) {
    handleDragStart(e.clientY);
  }

  function onPointerMove(e) {
    handleDragMove(e.clientY);
  }

  function onPointerUp() {
    handleDragEnd();
  }

  function onPointerCancel() {
    handleDragEnd();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!productId) {
      setError(t("review_form_error_product"));
      return;
    }

    if (!rating) {
      setError(t("review_form_error_rating"));
      return;
    }

    if (!comment.trim()) {
      setError(t("review_form_error_comment"));
      return;
    }

    try {
      setSubmitting(true);

      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        try {
          await loginWithGoogle();
        } catch (e) {
          console.error("login before submit review error:", e);
          setError(t("review_form_error_auth"));
        }
        return;
      }

      const token = await user.getIdToken();

      const res = await fetch(
        `${API_BASE}/api/catalog/reviews?lang=${encodeURIComponent(lang)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            product_id: productId,
            rating,
            comment: comment.trim(),
            name: name.trim(),
            email: email.trim(),
          }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Failed to create review");
      }

      if (onCreated) {
        await onCreated(data?.item || null);
      }

      onClose?.();
    } catch (err) {
      setError(err?.message || t("review_form_error_default"));
    } finally {
      setSubmitting(false);
    }
  }

  if (!mounted) return null;

  const overlayClass = `lcsOverlay ${open && !closing ? "isOpen" : "isClosing"}`;

  const sheetStyle = dragY
    ? {
        transform: `translateY(${dragY}px)`,
        transition: "none",
      }
    : undefined;

  return (
    <div
      className={overlayClass}
      style={{ paddingTop: topOffset }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="lcsSheet"
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        style={sheetStyle}
      >
        <div
          className="lcsHandleWrap"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <div className="lcsHandle" />
        </div>

        <div
          className="lcsHeader"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          <div className="lcsTitle">{productTitle}</div>

          <button
            type="button"
            className="lcsCloseBtn"
            onClick={onClose}
            aria-label={t("close")}
            title={t("close")}
          >
            <FiX />
          </button>
        </div>

        <form className="lcsForm" onSubmit={handleSubmit}>
          <div className="lcsRatingTitle">{t("review_form_rating_label")}</div>

          <div className="lcsStarsRow">
  {[1, 2, 3, 4, 5].map((value) => {
    const active = rating >= value;

    return (
      <button
        key={value}
        type="button"
        className="lcsStarBtn"
        onClick={() => setRating(value)}
        aria-label={`${value}`}
      >
        {active ? (
          <BsStarFill className="lcsStarActive" />
        ) : (
          <BsStar className="lcsStarIdle" />
        )}
      </button>
    );
  })}
</div>

          <input
  className="lcsInput lcsInputLocked"
  type="text"
  placeholder={t("review_form_name_placeholder")}
  value={name}
  readOnly
  autoComplete="name"
/>

<input
  className="lcsInput lcsInputLocked"
  type="email"
  placeholder={t("review_form_email_placeholder")}
  value={email}
  readOnly
  autoComplete="email"
/>

          <textarea
  className="lcsTextarea"
  placeholder={t("review_form_comment_placeholder")}
  value={comment}
  onChange={(e) => {
    setComment(e.target.value);

    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }}
  rows={1}
/>

          {error ? <div className="lcsError">{error}</div> : null}

          <button
            type="submit"
            className="lcsSubmitBtn"
            disabled={submitting}
          >
            {submitting
              ? t("review_form_submitting")
              : t("review_form_submit")}
          </button>
        </form>
      </div>
    </div>
  );
}